import axios from "axios";
import api from "./api";
import superAdminApi from "./superAdminApi";
import SuperAdminStorageService from "./SuperAdminStorageService";
import { API_BASE_URL } from "../constants/api-urls";
import { loaderService } from "../services/loaderService";

/** Hub home + read-only master location lookups are public; Super Admin JWT must not be sent. */
const publicMasterApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
});

const isPublicMasterLookup = (url) => {
  const path = String(url || "").split("?")[0];
  return (
    path === "/master/company-Country" ||
    /^\/master\/company-State\/\d+$/i.test(path) ||
    /^\/master\/company-City\/\d+$/i.test(path)
  );
};

const isHubHomePublic = (url) => {
  const path = String(url || "").split("?")[0];
  return path === "/hub-home" || path.startsWith("/hub-home/");
};

const isPlatformPublic = (url) => {
  const path = String(url || "").split("?")[0];
  return path === "/public/platform" || path.startsWith("/public/platform/");
};

const getClient = (url) => {
  // Platform public write endpoints use optional auth — send the buyer JWT when present.
  // Still avoid Super Admin JWT on public endpoints.
  if (isPlatformPublic(url)) {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/super-admin") &&
      SuperAdminStorageService.isAuthenticated()
    ) {
      return publicMasterApi;
    }
    return api;
  }

  if (isPublicMasterLookup(url) || isHubHomePublic(url)) {
    return publicMasterApi;
  }
  if (
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/super-admin") &&
    SuperAdminStorageService.isAuthenticated()
  ) {
    return superAdminApi;
  }
  return api;
};

class CommonService {
  async get(url, params = {}, options = {}) {
    const silent = options.silent === true;
    const { silent: _silent, signal, ...axiosConfig } = options;
    if (!silent) loaderService.show();
    try {
      const res = await getClient(url).get(url, { params, signal, ...axiosConfig });
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      if (!silent) loaderService.hide();
    }
  }

  async post(url, data, config = {}) {
    const silent = config.silent === true;
    const { silent: _s, ...axiosConfig } = config;
    if (!silent) loaderService.show();
    try {
      const res = await getClient(url).post(url, data, axiosConfig);
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      if (!silent) loaderService.hide();
    }
  }

  async put(url, data, options = {}) {
    const silent = options.silent === true;
    if (!silent) loaderService.show();
    try {
      const res = await getClient(url).put(url, data);
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      if (!silent) loaderService.hide();
    }
  }

  async patch(url, data, config = {}) {
    const silent = config.silent === true;
    const { silent: _s, ...axiosConfig } = config;
    if (!silent) loaderService.show();
    try {
      const res = await getClient(url).patch(url, data, axiosConfig);
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      if (!silent) loaderService.hide();
    }
  }

  async delete(url, options = {}) {
    const silent = options.silent === true;
    if (!silent) loaderService.show();
    try {
      const res = await getClient(url).delete(url);
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      if (!silent) loaderService.hide();
    }
  }
}

export default new CommonService();
